import React,{useState,useEffect,useCallback,useRef,useMemo} from 'react';
import {orderAPI,customerAPI,productAPI,branchAPI,invoiceAPI,inventoryAPI,currencyAPI,settingsAPI} from '../utils/api';
import {SearchBar,FilterButtons,fmt,fmtDate} from '../components/common';
import {useLang} from '../i18n';
import {Plus,X,Package,Search,ChevronDown,Trash2,Save,FileText,
  RefreshCw,Edit2,Layers,ArrowLeft,ChevronRight,ArrowRightCircle,CheckCircle,Printer} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
const NoPerm=({action})=>(<div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--dm)',borderRadius:8,fontSize:12,color:'var(--danger)',marginBottom:12,borderRadius:8}}><span>🔒</span> You don't have permission to {action}.</div>);

/* ── useWindowWidth hook ─────────────────────────────────────── */
function useWindowWidth(){
  const[w,setW]=useState(()=>typeof window!=='undefined'?window.innerWidth:1200);
  useEffect(()=>{
    const h=()=>setW(window.innerWidth);
    window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);
  },[]);
  return w;
}

/* ── Status config ───────────────────────────────────────────── */
const ALL_STATUSES=['draft','pending','confirmed','processing','shipped','completed','cancelled'];
const FILTER_TABS=['All',...ALL_STATUSES];
const S_CFG={
  draft:     {color:'#64748b',bg:'#f1f5f9',label:'Draft'},
  pending:   {color:'#d97706',bg:'#fef3c7',label:'Pending'},
  confirmed: {color:'#2563eb',bg:'#dbeafe',label:'Confirmed'},
  processing:{color:'#7c3aed',bg:'#ede9fe',label:'Processing'},
  shipped:   {color:'#0891b2',bg:'#cffafe',label:'Shipped'},
  completed: {color:'#16a34a',bg:'#dcfce7',label:'Completed'},
  cancelled: {color:'#dc2626',bg:'#fee2e2',label:'Cancelled'},
};

const SPill=({s,size=11})=>{
  const c=S_CFG[s]||{color:'#64748b',bg:'#f1f5f9',label:s};
  return(
    <span style={{display:'inline-flex',alignItems:'center',gap:4,background:c.bg,color:c.color,
      padding:'3px 10px',borderRadius:20,fontSize:size,fontWeight:700,textTransform:'capitalize',whiteSpace:'nowrap'}}>
      {c.label}
    </span>
  );
};

/* ── Product Picker ──────────────────────────────────────────── */
function ProductPicker({value,products,onSelect,disabled}){
  const {t}= useLang();
  const[open,setOpen]=useState(false);
  const[q,setQ]=useState('');
  const wrapRef=useRef();const inputRef=useRef();
  const selected=useMemo(()=>products.find(p=>p.id===value),[products,value]);

  useEffect(()=>{
    const h=e=>{if(wrapRef.current&&!wrapRef.current.contains(e.target))setOpen(false)};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);
  useEffect(()=>{if(open&&inputRef.current)inputRef.current.focus()},[open]);

  const filtered=useMemo(()=>{
    const ql=q.trim().toLowerCase();
    if(!ql)return products.slice(0,40);
    return products.filter(p=>(p.name||'').toLowerCase().includes(ql)||(p.sku||'').toLowerCase().includes(ql)).slice(0,30);
  },[q,products]);

  return(
    <div ref={wrapRef} style={{position:'relative'}}>
      <div onClick={()=>!disabled&&setOpen(v=>!v)} style={{
        display:'flex',alignItems:'center',gap:8,padding:'8px 10px',
        borderRadius:8,border:`1.5px solid ${open?'var(--primary)':'var(--border)'}`,
        background:'var(--s2)',cursor:disabled?'default':'pointer',minHeight:40,
        userSelect:'none',transition:'border-color .15s'}}>
        {selected?(
          <div style={{flex:1,display:'flex',alignItems:'center',gap:8,minWidth:0}}>
            {selected.image_url
              ?<img src={selected.image_url} alt="" style={{width:24,height:24,borderRadius:5,objectFit:'cover',flexShrink:0}}/>
              :<div style={{width:24,height:24,borderRadius:5,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={11} color="var(--primary)"/></div>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{selected.name}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>{selected.sku} · {selected.uom?.abbreviation||'—'}</div>
            </div>
          </div>
        ):<span style={{fontSize:13,color:'var(--td)',flex:1}}>— Select product —</span>}
        {!disabled&&<ChevronDown size={13} color="var(--td)" style={{flexShrink:0,transition:'transform .15s',transform:open?'rotate(180deg)':'none'}}/>}
      </div>
      {open&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:9000,
          background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:10,
          boxShadow:'0 8px 40px rgba(0,0,0,.2)',display:'flex',flexDirection:'column',maxHeight:260}}>
          <div style={{padding:'8px 10px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
            <Search size={13} color="var(--td)"/>
            <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)} placeholder={t('orders.searchProduct')}
              style={{border:'none',background:'transparent',color:'var(--text)',fontSize:13,outline:'none',flex:1}}
              onKeyDown={e=>e.key==='Escape'&&setOpen(false)}/>
            {q&&<button style={{border:'none',background:'none',cursor:'pointer',color:'var(--td)',padding:0}} onClick={()=>setQ('')}><X size={11}/></button>}
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.length===0
              ?<div style={{padding:'20px',textAlign:'center',color:'var(--td)',fontSize:13}}>
                <Package size={24} style={{opacity:.2,display:'block',margin:'0 auto 8px'}}/>
                {products.length===0?'No products stocked at this branch':'No products found'}
                {products.length===0&&<div style={{fontSize:11,marginTop:4}}>Add stock at this branch first</div>}
              </div>
              :filtered.map(p=>(
                <div key={p.id} onClick={()=>{onSelect(p);setOpen(false);setQ('')}}
                  style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',cursor:'pointer',borderBottom:'1px solid var(--border)'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--s2)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  {p.image_url
                    ?<img src={p.image_url} alt="" style={{width:32,height:32,borderRadius:7,objectFit:'cover',flexShrink:0}}/>
                    :<div style={{width:32,height:32,borderRadius:7,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={14} color="var(--primary)"/></div>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600}}>{p.name}</div>
                    <div style={{fontSize:11,color:'var(--td)'}}>SKU: {p.sku}{p.uom?` · ${p.uom.abbreviation}`:''}</div>
                  </div>
                  <span style={{fontSize:12,fontWeight:700,color:'var(--primary)',flexShrink:0}}>{fmt(p.sale_price)}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mobile Line Item Card (edit) ────────────────────────────── */
function MobileItemCard({item,idx,products,onChange,onRemove}){
  const prod=useMemo(()=>products.find(p=>p.id===item.product_id),[products,item.product_id]);
  const lineTotal=+((item.quantity||0)*(item.unit_price||0)).toFixed(2);
  const uomOpts=useMemo(()=>{
    if(!prod)return[];
    const opts=[];
    if(prod.uom)opts.push({id:prod.uom.id,label:prod.uom.abbreviation,price:parseFloat(prod.sale_price)||0});
    (prod.uoms||[]).forEach(pu=>{if(pu.uom&&(!prod.uom||pu.uom.id!==prod.uom.id))opts.push({id:pu.uom.id,label:pu.uom.abbreviation,price:parseFloat(pu.price)||parseFloat(prod.sale_price)||0})});
    return opts;
  },[prod]);
  const handleProductSelect=p=>onChange(idx,{product_id:p.id,unit_price:parseFloat(p.sale_price)||0,uom_id:p.uom_id||null,quantity:item.quantity||1});
  const handleUomChange=uomId=>{const opt=uomOpts.find(o=>o.id===parseInt(uomId));if(opt)onChange(idx,{uom_id:opt.id,unit_price:opt.price})};

  return(
    <div style={{background:'var(--s2)',borderRadius:12,padding:14,marginBottom:10,border:'1px solid var(--border)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <span style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5}}>Line {idx+1}</span>
        <button onClick={()=>onRemove(idx)} style={{background:'var(--dm)',border:'none',borderRadius:6,cursor:'pointer',color:'var(--danger)',padding:'4px 8px',display:'flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600}}>
          <X size={11}/> Remove
        </button>
      </div>
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,display:'block',marginBottom:5}}>Product</label>
        <ProductPicker value={item.product_id} products={products} onSelect={handleProductSelect}/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,display:'block',marginBottom:5}}>UOM</label>
          <select value={String(item.uom_id||prod?.uom_id||'')} onChange={e=>handleUomChange(e.target.value)}
            disabled={uomOpts.length<=1}
            style={{fontSize:13,padding:'8px 10px',height:40,width:'100%',background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)'}}>
            {uomOpts.length>0?uomOpts.map(o=><option key={o.id} value={o.id}>{o.label}</option>):<option value="">{prod?.uom?.abbreviation||'—'}</option>}
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,display:'block',marginBottom:5}}>Qty</label>
          <input type="number" min={0} value={item.quantity??1} onChange={e=>onChange(idx,{quantity:parseFloat(e.target.value)||0})}
            style={{fontSize:13,padding:'8px 10px',height:40,width:'100%',background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',textAlign:'right',boxSizing:'border-box'}}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,display:'block',marginBottom:5}}>Price</label>
          <input type="number" min={0} step={0.01} value={item.unit_price??0} onChange={e=>onChange(idx,{unit_price:parseFloat(e.target.value)||0})}
            style={{fontSize:13,padding:'8px 10px',height:40,width:'100%',background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',textAlign:'right',boxSizing:'border-box'}}/>
        </div>
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:10,paddingTop:10,borderTop:'1px solid var(--border)'}}>
        <span style={{fontSize:11,color:'var(--td)',marginRight:8}}>Amount</span>
        <span style={{fontSize:15,fontWeight:800,color:'var(--primary)'}}>{fmt(lineTotal)}</span>
      </div>
    </div>
  );
}

/* ── Desktop Line Item Row ───────────────────────────────────── */
function DesktopItemRow({item,idx,isFirst,products,onChange,onRemove,readOnly}){
  const prod=useMemo(()=>products.find(p=>p.id===item.product_id),[products,item.product_id]);
  const lineTotal=+((item.quantity||0)*(item.unit_price||0)).toFixed(2);
  const uomOpts=useMemo(()=>{
    if(!prod)return[];
    const opts=[];
    if(prod.uom)opts.push({id:prod.uom.id,label:prod.uom.abbreviation,price:parseFloat(prod.sale_price)||0});
    (prod.uoms||[]).forEach(pu=>{if(pu.uom&&(!prod.uom||pu.uom.id!==prod.uom.id))opts.push({id:pu.uom.id,label:pu.uom.abbreviation,price:parseFloat(pu.price)||parseFloat(prod.sale_price)||0})});
    return opts;
  },[prod]);
  const handleProductSelect=p=>onChange(idx,{product_id:p.id,unit_price:parseFloat(p.sale_price)||0,uom_id:p.uom_id||null,quantity:item.quantity||1});
  const handleUomChange=uomId=>{const opt=uomOpts.find(o=>o.id===parseInt(uomId));if(opt)onChange(idx,{uom_id:opt.id,unit_price:opt.price})};
  const H=({children})=>isFirst?<div style={{fontSize:9,color:'var(--td)',fontWeight:700,letterSpacing:.5,marginBottom:3,textTransform:'uppercase'}}>{children}</div>:null;

  if(readOnly){
    return(
      <tr style={{borderBottom:'1px solid var(--border)'}}>
        <td style={{padding:'10px 12px',fontSize:12,color:'var(--td)',textAlign:'center'}}>{idx+1}</td>
        <td style={{padding:'10px 12px'}}>
          <div style={{fontSize:13,fontWeight:600}}>{prod?.name||'—'}</div>
          <div style={{fontSize:11,color:'var(--td)'}}>{prod?.sku}</div>
        </td>
        <td style={{padding:'10px 12px',fontSize:12,color:'var(--td)',textAlign:'center'}}>{item.uom_id?uomOpts.find(o=>o.id===item.uom_id)?.label:prod?.uom?.abbreviation||'—'}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:600}}>{item.quantity}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13}}>{fmt(item.unit_price)}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:800,color:'var(--primary)'}}>{fmt(lineTotal)}</td>
      </tr>
    );
  }

  return(
    <div style={{marginBottom:6}}>
      <div style={{display:'grid',gridTemplateColumns:'minmax(160px,2fr) 80px 80px 110px 100px 30px',gap:5,alignItems:'start'}}>
        <div><H>Product / SKU</H><ProductPicker value={item.product_id} products={products} onSelect={handleProductSelect}/></div>
        <div>
          <H>UOM</H>
          <select value={String(item.uom_id||prod?.uom_id||'')} onChange={e=>handleUomChange(e.target.value)} disabled={uomOpts.length<=1}
            style={{fontSize:12,padding:'6px 8px',height:40,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)'}}>
            {uomOpts.length>0?uomOpts.map(o=><option key={o.id} value={o.id}>{o.label}</option>):<option value="">{prod?.uom?.abbreviation||'—'}</option>}
          </select>
        </div>
        <div>
          <H>Qty</H>
          <input type="number" min={0} step={1} value={item.quantity??1} onChange={e=>onChange(idx,{quantity:parseFloat(e.target.value)||0})}
            style={{fontSize:13,padding:'6px 8px',height:40,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',textAlign:'right',boxSizing:'border-box'}}/>
        </div>
        <div>
          <H>Unit Price</H>
          <input type="number" min={0} step={0.01} value={item.unit_price??0} onChange={e=>onChange(idx,{unit_price:parseFloat(e.target.value)||0})}
            style={{fontSize:13,padding:'6px 8px',height:40,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',textAlign:'right',boxSizing:'border-box'}}/>
        </div>
        <div>
          <H>Amount</H>
          <div style={{height:40,display:'flex',alignItems:'center',justifyContent:'flex-end',fontSize:13,fontWeight:800,paddingRight:6,background:'var(--pm)',borderRadius:8,color:'var(--primary)'}}>
            {fmt(lineTotal)}
          </div>
        </div>
        <div>
          {isFirst&&<div style={{height:17}}/>}
          <button onClick={()=>onRemove(idx)} style={{color:'var(--danger)',background:'none',border:'none',cursor:'pointer',padding:'6px',height:40,width:30,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:6}}>
            <X size={13}/>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Status Stepper ──────────────────────────────────────────── */
function StatusStepper({current,onChange,disabled,mobile=false}){
  const curIdx=ALL_STATUSES.indexOf(current);
  return(
    <div style={{padding:mobile?'12px':'12px 16px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.5,marginBottom:8,textTransform:'uppercase'}}>Change Status</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:mobile?5:6}}>
        {ALL_STATUSES.map((s,i)=>{
          const cfg=S_CFG[s];const isCurrent=s===current;const isDone=i<curIdx;
          return(
            <button key={s} onClick={()=>!disabled&&s!==current&&onChange(s)} disabled={disabled||isCurrent}
              style={{display:'flex',alignItems:'center',gap:4,padding:mobile?'6px 10px':'5px 11px',borderRadius:20,
                fontSize:mobile?12:11,fontWeight:700,cursor:(disabled||isCurrent)?'default':'pointer',transition:'all .15s',
                border:`1.5px solid ${isCurrent?cfg.color:'var(--border)'}`,
                background:isCurrent?cfg.bg:isDone?'var(--sm)':'var(--surface)',
                color:isCurrent?cfg.color:isDone?'var(--success)':'var(--td)',opacity:disabled?0.6:1}}>
              {isCurrent&&<span style={{width:6,height:6,borderRadius:'50%',background:cfg.color,flexShrink:0}}/>}
              {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Order Detail / Form Panel ───────────────────────────────── */
const BLANK_ITEM={product_id:null,quantity:1,unit_price:0,uom_id:null};
const BLANK_FORM={customer_id:'',branch_id:'',order_date:new Date().toISOString().split('T')[0],tax_rate:0,discount_amount:0,notes:'',status:'draft',items:[{...BLANK_ITEM}]};

function OrderPanel({order,isNew,customers,products,branches,branchInventory,onSaved,onDeleted,onClose,onPrint,isMobile}){
  const{isOwner,myBranchId,myBranch,hasPermission}=useAuth();
  const {t} = useLang();
  // Panel-level permissions
  const can={
    edit:hasPermission('orders.edit')||isOwner,
    delete:hasPermission('orders.delete')||isOwner,
    approve:hasPermission('orders.approve')||isOwner,
    cancel:hasPermission('orders.cancel')||isOwner,
  };

  // Dual-currency helper — reads from parent via prop or uses fallback
  const[panelKhrRate,setPanelKhrRate]=useState(4100);
  useEffect(()=>{currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setPanelKhrRate(parseFloat(rt.rate)||4100);}).catch(()=>{});},[]);
  const fmtKHR=n=>`≈ ៛${Math.round((parseFloat(n)||0)*panelKhrRate).toLocaleString()}`;

  // Determine the default branch for new orders:
  // non-owners always use their assigned branch; owners start empty
  const defaultBranchId=(!isOwner&&myBranchId)?String(myBranchId):'';

  const[form,setForm]=useState(()=>{
    if(isNew)return{...JSON.parse(JSON.stringify(BLANK_FORM)),branch_id:defaultBranchId};
    return{
      customer_id:order.customer_id||'',branch_id:order.branch_id||'',
      order_date:order.order_date||new Date().toISOString().split('T')[0],
      tax_rate:parseFloat(order.tax_rate)||0,discount_amount:parseFloat(order.discount_amount)||0,
      notes:order.notes||'',status:order.status||'draft',
      items:(order.items||[]).map(i=>({product_id:i.product_id,quantity:parseFloat(i.quantity),unit_price:parseFloat(i.unit_price),uom_id:i.uom_id||null})),
    };
  });
  const[saving,setSaving]=useState(false);
  const[statusChanging,setStatusChanging]=useState(false);
  const[editMode,setEditMode]=useState(isNew);
  const[delConfirm,setDelConfirm]=useState(false);
  const[converting,setConverting]=useState(false);
  const[invoiceNum,setInvoiceNum]=useState(order?.linked_invoice_number||null);

  const sf=(k,v)=>setForm(f=>({...f,[k]:v}));
  const addItem=()=>setForm(f=>({...f,items:[...f.items,{...BLANK_ITEM}]}));
  const rmItem=i=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}));
  const upItem=(i,patch)=>setForm(f=>{const items=[...f.items];items[i]={...items[i],...patch};return{...f,items}});

  // For owners: load inventory when they change branch in the form
  const[dynInventory,setDynInventory]=useState([]);
  const[dynLoading,setDynLoading]=useState(false);
  useEffect(()=>{
    if(!isOwner||!form.branch_id)return;
    setDynLoading(true);
    inventoryAPI.getAll({branch_id:form.branch_id,limit:1000})
      .then(r=>setDynInventory(r.data.data||[]))
      .catch(()=>{})
      .finally(()=>setDynLoading(false));
  },[isOwner,form.branch_id]);

  const custOpts=[{value:'',label:'— Select Customer —'},...customers.map(c=>({value:String(c.id),label:c.name}))];
  const brOpts=[{value:'',label:'— Select Branch —'},...branches.map(b=>({value:String(b.id),label:b.name}))];

  // Branch-filtered products:
  // - Non-owners: branchInventory prop (pre-loaded for their fixed branch)
  // - Owners selecting a branch: dynInventory loaded when form.branch_id changes
  // - No branch (owner): show all
  const branchProductIds=useMemo(()=>{
    if(!isOwner&&branchInventory&&branchInventory.length>0){
      return new Set(branchInventory.map(i=>i.product_id));
    }
    if(isOwner&&dynInventory.length>0){
      return new Set(dynInventory.map(i=>i.product_id));
    }
    return null;
  },[isOwner,branchInventory,dynInventory]);

  const branchProducts=useMemo(()=>{
    if(!branchProductIds||branchProductIds.size===0)return products; // no inventory = show all
    return products.filter(p=>branchProductIds.has(p.id));
  },[products,branchProductIds]);

  const validItems=form.items.filter(i=>i.product_id&&(i.quantity||0)>0);
  const subtotal=+validItems.reduce((s,i)=>s+(i.quantity||0)*(i.unit_price||0),0).toFixed(2);
  const taxAmt=+(subtotal*(parseFloat(form.tax_rate||0)/100)).toFixed(2);
  const total=+(subtotal+taxAmt-parseFloat(form.discount_amount||0)).toFixed(2);

  const save=async()=>{
    if(!form.customer_id)return toast.error('Please select a customer');
    if(!validItems.length)return toast.error('Add at least one product');
    setSaving(true);
    try{
      const d={customer_id:parseInt(form.customer_id),branch_id:form.branch_id?parseInt(form.branch_id):null,
        order_date:form.order_date,tax_rate:parseFloat(form.tax_rate)||0,discount_amount:parseFloat(form.discount_amount)||0,
        notes:form.notes||'',subtotal,tax_amount:taxAmt,total_amount:total,
        items:validItems.map(i=>({product_id:parseInt(i.product_id),quantity:parseFloat(i.quantity),
          unit_price:parseFloat(i.unit_price),uom_id:i.uom_id?parseInt(i.uom_id):null,discount_amount:0}))};
      if(isNew){await orderAPI.create(d);toast.success('Order created');}
      else{await orderAPI.update(order.id,d);toast.success('Order updated');}
      setEditMode(false);onSaved();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save')}
    finally{setSaving(false)}
  };

  const changeStatus=async s=>{
    setStatusChanging(true);
    try{await orderAPI.updateStatus(order.id,s);toast.success(`Status → ${s}`);setForm(f=>({...f,status:s}));onSaved();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
    finally{setStatusChanging(false)}
  };

  const doDelete=async()=>{
    try{await orderAPI.delete(order.id);toast.success(t('common.deleted'));onDeleted();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
  };

  const createInvoice=async()=>{
    if(!order.branch_id)return toast.error('Order needs a branch before creating an invoice');
    setConverting(true);
    try{
      const{data:inv}=await invoiceAPI.createFromOrder(order.id);
      setInvoiceNum(inv.invoice_number);
      setForm(f=>({...f,status:'completed'}));
      if(inv.stock_deducted){
        toast.success(`Invoice ${inv.invoice_number} created — stock deducted for ${inv.items_deducted} item(s)`);
      }else{
        toast.success(`Invoice ${inv.invoice_number} created`);
      }
      onSaved();
    }catch(e){
      const err=e.response?.data;
      if(err?.details&&Array.isArray(err.details)){
        // Stock validation failed — show each item error
        toast.error(err.error||'Insufficient stock',{duration:5000});
        err.details.forEach(d=>toast.error(`• ${d}`,{duration:7000}));
      }else{
        toast.error(err?.error||'Failed to create invoice');
      }
    }
    finally{setConverting(false)}
  };

  const inputCls={fontSize:14,padding:'10px 12px',height:44,width:'100%',background:'var(--s2)',
    border:'1.5px solid var(--border)',borderRadius:9,color:'var(--text)',boxSizing:'border-box'};

  /* ── mobile-friendly select/input for header fields ── */
  const MobileField=({label,children})=>(
    <div>
      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>{label}</label>
      {children}
    </div>
  );

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
      {/* Header */}
      <div style={{padding:isMobile?'14px 16px':'16px 20px',borderBottom:'1px solid var(--border)',
        display:'flex',alignItems:'center',gap:10,flexShrink:0,background:'var(--surface)'}}>
        {isMobile&&(
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:'4px',display:'flex',flexShrink:0}}>
            <ArrowLeft size={20}/>
          </button>
        )}
        <div style={{flex:1,minWidth:0}}>
          {isNew
            ?<div style={{fontSize:16,fontWeight:800}}>{t('orders.newOrder')}</div>
            :<div>
              <div style={{fontSize:15,fontWeight:800}}>{order.order_number}</div>
              <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{fmtDate(order.order_date)} · {order.customer?.name}</div>
              <div style={{display:'flex',gap:10,marginTop:2,flexWrap:'wrap'}}>
                {order.creator&&<span style={{fontSize:10,color:'var(--td)'}}>
                  📝 Created by: <span style={{color:'var(--primary)',fontWeight:600}}>{order.creator.first_name} {order.creator.last_name}</span>
                </span>}
                {order.updater&&<span style={{fontSize:10,color:'var(--td)'}}>
                  ✏️ Edited by: <span style={{color:'var(--accent)',fontWeight:600}}>{order.updater.first_name} {order.updater.last_name}</span>
                </span>}
              </div>
            </div>}
        </div>
        {!isNew&&<SPill s={form.status}/>}
        {!isNew&&!editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>onPrint&&onPrint(order)} title="Print Order" style={{padding:'5px 8px'}}><Printer size={13}/></button>}
        {/* Create Invoice button — shown for confirmed/processing/shipped orders that haven't been converted yet */}
        {!isNew&&['confirmed','processing','shipped'].includes(form.status)&&!editMode&&!invoiceNum&&(
          <button className="btn btn-primary btn-sm" onClick={createInvoice} disabled={converting}
            style={{gap:4,fontSize:12,whiteSpace:'nowrap'}}>
            {converting
              ?<><RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>{!isMobile&&' Converting…'}</>
              :<><ArrowRightCircle size={13}/>{!isMobile&&' Create Invoice'}</>}
          </button>
        )}
        {!isNew&&(form.status==='draft')&&!editMode&&can.edit&&(
          <button className="btn btn-ghost btn-sm" onClick={()=>setEditMode(true)} style={{gap:4,fontSize:12}}>
            <Edit2 size={13}/>{!isMobile&&' Edit'}
          </button>
        )}
        {!isNew&&form.status==='draft'&&!editMode&&can.delete&&(
          delConfirm
            ?<div style={{display:'flex',gap:5,alignItems:'center'}}>
                <button className="btn btn-danger btn-sm" style={{fontSize:11}} onClick={doDelete}>{t('common.delete')}</button>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setDelConfirm(false)}>No</button>
              </div>
            :<button className="btn btn-ghost btn-sm" style={{color:'var(--danger)',padding:'5px 7px'}} onClick={()=>setDelConfirm(true)}>
                <Trash2 size={14}/>
              </button>
        )}
        {!isMobile&&(
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:4,display:'flex'}}>
            <X size={18}/>
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{flex:1,overflowY:'auto',padding:isMobile?'16px':'20px'}}>

        {/* Invoice-created success banner */}
        {invoiceNum&&(
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'11px 14px',
            background:'var(--sm)',border:'1px solid var(--success)',borderRadius:10,marginBottom:14}}>
            <CheckCircle size={16} color="var(--success)"/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:'var(--success)'}}>✅ Invoice Created / វិក្កយបត្រត្រូវបានបង្កើត</div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
                <span style={{fontSize:11,fontFamily:'monospace',fontWeight:700,padding:'1px 8px',borderRadius:5,background:'rgba(34,197,94,.15)',color:'var(--success)',border:'1px solid rgba(34,197,94,.3)'}}>{invoiceNum}</span>
                <span style={{fontSize:11,color:'var(--td)'}}>→ Go to Invoices to view &amp; pay</span>
              </div>
            </div>
          </div>
        )}

        {/* Status stepper */}
        {!isNew&&(
          <div style={{marginBottom:16}}>
            <StatusStepper current={form.status} onChange={changeStatus} disabled={statusChanging} mobile={isMobile}/>
          </div>
        )}

        {/* Create Invoice CTA — shown for ready orders not yet converted */}
        {!isNew&&['confirmed','processing','shipped'].includes(form.status)&&!editMode&&!invoiceNum&&(
          <div style={{display:'flex',alignItems:'center',gap:14,padding:'14px 16px',
            background:'linear-gradient(135deg,var(--pm) 0%,var(--s2) 100%)',
            border:'1.5px solid var(--primary)',borderRadius:12,marginBottom:16}}>
            <div style={{width:40,height:40,borderRadius:10,background:'var(--primary)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <ArrowRightCircle size={20} color="#fff"/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:800,color:'var(--primary)'}}>{t('orders.readyToInvoice')}</div>
              <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{t('orders.createInvoiceDesc')}</div>
            </div>
            <button className="btn btn-primary" onClick={createInvoice} disabled={converting}
              style={{flexShrink:0,gap:6,height:38,fontSize:13}}>
              {converting
                ?<><RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/> Creating…</>
                :<><ArrowRightCircle size={14}/> Create Invoice</>}
            </button>
          </div>
        )}

        {/* Header fields — 2-col on desktop, 1-col on mobile */}
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:isMobile?12:'0 16px',marginBottom:16}}>
          <MobileField label="Customer *">
            <select value={String(form.customer_id||'')} onChange={e=>sf('customer_id',e.target.value)}
              style={{...inputCls,border:!form.customer_id&&isNew?'1.5px solid var(--primary)':'1.5px solid var(--border)'}}>
              {custOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {isNew&&customers.length===0&&<div style={{fontSize:10,color:'var(--warning)',marginTop:3}}>⚠ No customers yet — add customers first</div>}
            {isNew&&customers.length>0&&!form.customer_id&&<div style={{fontSize:10,color:'var(--primary)',marginTop:3}}>👤 {customers.length} customer{customers.length!==1?'s':''} available — select one</div>}
          </MobileField>
          <MobileField label="Branch">
            {branches.length<=1&&form.branch_id?(
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'10px 12px',background:'var(--pm)',border:'1.5px solid var(--primary)',borderRadius:9,fontSize:13,fontWeight:600,color:'var(--primary)',height:44,boxSizing:'border-box'}}>
                🔒 {branches.find(b=>String(b.id)===String(form.branch_id))?.name||myBranch?.name||'Your Branch'}
              </div>
            ):(
              <select value={String(form.branch_id||'')} onChange={e=>sf('branch_id',e.target.value)} style={inputCls}>
                {brOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </MobileField>
          <MobileField label={t('orders.orderDate')}>
            <input type="date" value={form.order_date||''} onChange={e=>sf('order_date',e.target.value)} style={inputCls}/>
          </MobileField>
          <MobileField label="Tax %">
            <input type="number" min={0} step={0.1} value={form.tax_rate||0} onChange={e=>sf('tax_rate',e.target.value)} style={inputCls}/>
          </MobileField>
        </div>

        {/* Line Items */}
        <div style={{marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'9px 12px',background:'var(--s2)',borderRadius:9,borderLeft:'3px solid var(--primary)'}}>
          <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
            <Layers size={13} color="var(--primary)"/>
            <span style={{fontSize:12,fontWeight:700,letterSpacing:.4}}>{t('orders.orderLines')}</span>
            <span style={{fontSize:11,color:'var(--td)'}}>{validItems.length} item{validItems.length!==1?'s':''}</span>
            {form.branch_id&&dynLoading&&<span style={{fontSize:10,color:'var(--primary)',display:'flex',alignItems:'center',gap:3}}><RefreshCw size={9} style={{animation:'spin 1s linear infinite'}}/> Loading products…</span>}
            {form.branch_id&&!dynLoading&&branchProductIds&&branchProductIds.size>0&&branchProducts.length<products.length&&<span style={{fontSize:10,color:'var(--success)',fontWeight:600}}>{branchProducts.length} products stocked at branch</span>}
          </div>
          {editMode&&<button className="btn btn-ghost btn-sm" onClick={addItem} style={{fontSize:12,gap:4}}><Plus size={13}/> {t('common.add')}</button>}
        </div>

        {editMode?(
          isMobile?(
            <div style={{marginBottom:12}}>
              {form.items.length===0
                ?<div style={{padding:24,textAlign:'center',color:'var(--td)',fontSize:13,background:'var(--s2)',borderRadius:10}}>No lines — tap Add</div>
                :form.items.map((item,i)=><MobileItemCard key={i} item={item} idx={i} products={branchProducts} onChange={upItem} onRemove={rmItem}/>)}
            </div>
          ):(
            <div style={{background:'var(--surface)',borderRadius:9,border:'1px solid var(--border)',padding:'10px 10px 4px',marginBottom:12}}>
              {form.items.length===0
                ?<div style={{padding:20,textAlign:'center',color:'var(--td)',fontSize:13}}>No lines — click Add</div>
                :form.items.map((item,i)=><DesktopItemRow key={i} item={item} idx={i} isFirst={i===0} products={branchProducts} onChange={upItem} onRemove={rmItem} readOnly={false}/>)}
            </div>
          )
        ):(
          /* Read-only view */
          isMobile?(
            <div style={{marginBottom:12}}>
              {(form.items||[]).filter(i=>i.product_id).map((item,i)=>{
                const prod=products.find(p=>p.id===item.product_id);
                const lineTotal=+((item.quantity||0)*(item.unit_price||0)).toFixed(2);
                return(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'12px',background:'var(--s2)',borderRadius:10,marginBottom:6,border:'1px solid var(--border)'}}>
                    <div style={{width:36,height:36,borderRadius:8,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <Package size={15} color="var(--primary)"/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{prod?.name||'—'}</div>
                      <div style={{fontSize:11,color:'var(--td)'}}>{item.quantity} × {fmt(item.unit_price)}</div>
                    </div>
                    <div style={{fontSize:14,fontWeight:800,color:'var(--primary)',flexShrink:0}}>{fmt(lineTotal)}</div>
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden',marginBottom:12}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'var(--s2)'}}>
                    {['#','Product','UOM','Qty','Unit Price','Amount'].map((h,i)=>(
                      <th key={h} style={{padding:'9px 12px',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:.4,color:'var(--td)',textAlign:i>=3?'right':'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(form.items||[]).filter(i=>i.product_id).map((item,i)=>(
                    <DesktopItemRow key={i} item={item} idx={i} isFirst={false} products={products} onChange={()=>{}} onRemove={()=>{}} readOnly={true}/>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Totals */}
        <div style={{background:'var(--s2)',borderRadius:10,padding:'14px 16px',marginBottom:14}}>
          {editMode&&(
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:isMobile?10:'0 12px',marginBottom:14}}>
              <div>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Tax %</label>
                <input type="number" min={0} step={0.1} value={form.tax_rate||0} onChange={e=>sf('tax_rate',e.target.value)} style={{...inputCls,background:'var(--surface)'}}/>
              </div>
              <div>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Discount ($)</label>
                <input type="number" min={0} step={0.01} value={form.discount_amount||0} onChange={e=>sf('discount_amount',e.target.value)} style={{...inputCls,background:'var(--surface)'}}/>
              </div>
            </div>
          )}
          <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}>
            <span style={{color:'var(--td)'}}>Subtotal</span><div style={{textAlign:'right'}}><span style={{fontWeight:600}}>{fmt(subtotal)}</span><div style={{fontSize:10,color:'var(--td)'}}>{fmtKHR(subtotal)}</div></div>
          </div>
          {parseFloat(form.tax_rate||0)>0&&(
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}>
              <span style={{color:'var(--td)'}}>Tax ({form.tax_rate}%)</span><span>{fmt(taxAmt)}</span>
            </div>
          )}
          {parseFloat(form.discount_amount||0)>0&&(
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}>
              <span style={{color:'var(--td)'}}>Discount</span><span style={{color:'var(--danger)'}}>-{fmt(form.discount_amount)}</span>
            </div>
          )}
          <div style={{display:'flex',justifyContent:'space-between',borderTop:'2px solid var(--border)',paddingTop:12,fontSize:18,fontWeight:900}}>
            <span>TOTAL</span><div style={{textAlign:'right'}}><span style={{color:'var(--primary)'}}>{fmt(total)}</span><div style={{fontSize:11,fontWeight:400,color:'var(--td)',marginTop:2}}>{fmtKHR(total)}</div></div>
          </div>
        </div>

        {(editMode||form.notes)&&(
          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Notes</label>
            <textarea value={form.notes||''} onChange={e=>sf('notes',e.target.value)} placeholder="Internal notes..."
              style={{width:'100%',fontSize:13,padding:'10px 12px',background:'var(--s2)',border:'1.5px solid var(--border)',
                borderRadius:9,color:'var(--text)',resize:'vertical',height:72,boxSizing:'border-box',fontFamily:'inherit'}}/>
          </div>
        )}

        {editMode&&(
          <div style={{display:'flex',gap:10,paddingTop:16,borderTop:'1px solid var(--border)'}}>
            {!isNew&&<button className="btn btn-ghost" onClick={()=>setEditMode(false)} style={{flex:1,height:46,fontSize:14}}>{t('common.cancel')}</button>}
            <button className="btn btn-primary" onClick={save} disabled={saving} style={{flex:2,height:46,fontSize:14,gap:7}}>
              {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> {t('common.saving')}</>:<><Save size={14}/> {isNew?t('orders.createOrder'):t('orders.saveChanges')}</>}
            </button>
          </div>
        )}
        <div style={{height:20}}/>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
function injectOrderPrintStyle(){
  const id='order-ps';if(document.getElementById(id))return;
  const s=document.createElement('style');s.id=id;
  s.textContent=`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Khmer:wght@400;700;900&display=swap');
    @media print{
      body *{visibility:hidden}
      #inv-print-area,#inv-print-area *{visibility:visible}
      #inv-print-area{position:fixed;inset:0;width:100%;background:#fff;padding:0;margin:0;font-family:'Noto Serif Khmer','Khmer OS',Arial,sans-serif;}
      @page{margin:12mm;size:A4}
    }
  `;
  document.head.appendChild(s);
}

/* ── Print Order Modal ─────────────────────────────────────────── */
function PrintOrderModal({order,settings,onClose}){
  const[khr,setKhr]=useState(4100);
  useEffect(()=>{
    currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setKhr(parseFloat(rt.rate)||4100);}).catch(()=>{});
  },[]);
  const fmtKHR=n=>`${Math.round((parseFloat(n)||0)*khr).toLocaleString()}`;
  const fmtDate2=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}):'—';

  const subtotal=parseFloat(order.subtotal||0);
  const disc=parseFloat(order.discount_amount||0);
  const tax=parseFloat(order.tax_amount||0);
  const total=parseFloat(order.total_amount||0);
  const items=order.items||[];
  const padded=[...items,...Array(Math.max(0,8-items.length)).fill(null)];
  const biz=settings||{};
  const cellStyle={border:'1px solid #000',padding:'4px 6px',fontSize:11,verticalAlign:'middle'};
  const thStyle={...cellStyle,fontWeight:700,textAlign:'center',fontSize:10};
  const PAGE={fontFamily:"'Noto Serif Khmer','Khmer OS','Battambang',Arial,sans-serif",background:'#fff',color:'#000',padding:'18px 24px',maxWidth:740,margin:'0 auto',fontSize:12};

  return(
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.75)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:800,maxHeight:'94vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:14}}>🖨 ការបញ្ជាទិញ / Sale Order — {order.order_number}</span>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" onClick={()=>{const el=document.getElementById('inv-print-area');if(!el)return;const html=`<!DOCTYPE html><html><head><meta charset='utf-8'/><title>Order</title><link href='https://fonts.googleapis.com/css2?family=Noto+Serif+Khmer:wght@400;700;900&display=swap' rel='stylesheet'><style>*{box-sizing:border-box;}body{font-family:'Noto Serif Khmer','Khmer OS',Arial,sans-serif;font-size:12px;color:#000;background:#fff;margin:0;padding:0;}@page{margin:12mm;size:A4;}table{border-collapse:collapse;}</style></head><body>${el.innerHTML}</body></html>`;const pw=window.open('','_blank','width=900,height=700');if(!pw){alert('Allow popups');return;}pw.document.write(html);pw.document.close();pw.focus();setTimeout(()=>{pw.print();pw.close();},800);}} style={{gap:5}}>
              <Printer size={13}/> Print / PDF
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14}/></button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',background:'#e5e7eb'}}>
          <div style={{padding:16}}>
            <div id="inv-print-area" style={PAGE}>
              {/* Header */}
              <div style={{textAlign:'center',marginBottom:8}}>
                {biz.logo_url&&<img src={biz.logo_url} alt="" style={{height:48,marginBottom:4,objectFit:'contain'}}/>}
                {biz.company_name&&<div style={{fontSize:13,fontWeight:700}}>{biz.company_name}</div>}
                {biz.company_address&&<div style={{fontSize:10}}>Address : {biz.company_address}</div>}
                {biz.company_phone&&<div style={{fontSize:10}}>ទូរស័ព្ទ(Telephone No) : {biz.company_phone}</div>}
                <div style={{fontSize:16,fontWeight:900,color:'#1a56db',marginTop:6}}>ការបញ្ជាទិញ / SALE ORDER</div>
                <div style={{borderTop:'2px solid #000',marginTop:4}}/>
              </div>
              {/* Meta */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:0,marginBottom:8,fontSize:11}}>
                <div style={{paddingRight:8}}>
                  {[['ឈ្មោះអតិថិជន (Customer)',order.customer?.name||'—'],['ទូរស័ព្ទ (Telephone)',order.customer?.phone||'—'],['អាសយដ្ឋាន (Address)',order.customer?.address||'—']].map(([l,v])=>(
                    <div key={l} style={{display:'flex',gap:4,marginBottom:3}}>
                      <span style={{color:'#555',minWidth:160,flexShrink:0,fontSize:10}}>{l}</span>
                      <span style={{fontWeight:600,fontSize:11}}>: {v}</span>
                    </div>
                  ))}
                </div>
                <div style={{paddingLeft:8}}>
                  {[['លេខបញ្ជាទិញ (Order No)',order.order_number||'—'],['កាលបរិច្ឆេទ (Date)',fmtDate2(order.order_date)],['រូបិយប័ណ្ណ (Currency)','ដុល្លារ (USD)'],['សាខា (Branch)',order.branch?.name||'—']].map(([l,v])=>(
                    <div key={l} style={{display:'flex',gap:4,marginBottom:3}}>
                      <span style={{color:'#555',minWidth:160,flexShrink:0,fontSize:10}}>{l}</span>
                      <span style={{fontWeight:600,fontSize:11}}>: {v}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Items table */}
              <table style={{width:'100%',borderCollapse:'collapse',marginBottom:0,border:'1px solid #000'}}>
                <thead>
                  <tr>
                    <th style={{...thStyle,width:28}}>ល.រ<br/><span style={{fontWeight:400}}>No</span></th>
                    <th style={{...thStyle,width:90}}>លេខកូដ<br/><span style={{fontWeight:400}}>Code</span></th>
                    <th style={{...thStyle}}>បរិយាយទំនិញ<br/><span style={{fontWeight:400}}>Description</span></th>
                    <th style={{...thStyle,width:55}}>បរិមាណ<br/><span style={{fontWeight:400}}>Qty</span></th>
                    <th style={{...thStyle,width:60}}>ឯកតា<br/><span style={{fontWeight:400}}>UoM</span></th>
                    <th style={{...thStyle,width:70}}>ថ្លៃដើម<br/><span style={{fontWeight:400}}>Unit Price</span></th>
                    <th style={{...thStyle,width:70}}>វិភាជន៍<br/><span style={{fontWeight:400}}>Amount</span></th>
                  </tr>
                </thead>
                <tbody>
                  {padded.map((item,i)=>(
                    <tr key={i} style={{height:22}}>
                      <td style={{...cellStyle,textAlign:'center',fontSize:10}}>{item?i+1:''}</td>
                      <td style={{...cellStyle,fontSize:10}}>{item?.product?.sku||''}</td>
                      <td style={{...cellStyle}}>
                        {item&&<>
                          <div style={{fontWeight:600,fontSize:11}}>{item.product?.name_kh||item.product?.name||'—'}</div>
                          {item.product?.name_kh&&<div style={{fontSize:9,color:'#555'}}>{item.product?.name}</div>}
                        </>}
                      </td>
                      <td style={{...cellStyle,textAlign:'center'}}>{item?.quantity||''}</td>
                      <td style={{...cellStyle,textAlign:'center',fontSize:10}}>{item&&(item.uom?.abbreviation||item.uom?.name||item.product?.uom?.abbreviation||'—')}</td>
                      <td style={{...cellStyle,textAlign:'right'}}>{item?`$${parseFloat(item.unit_price||0).toFixed(3)}`:''}</td>
                      <td style={{...cellStyle,textAlign:'right',fontWeight:item?600:400}}>{item?`$${parseFloat(item.line_total||(item.quantity*item.unit_price)||0).toFixed(2)}`:''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Totals */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',border:'1px solid #000',borderTop:'none'}}>
                <div style={{borderRight:'1px solid #000',padding:'6px 8px',fontSize:10}}>
                  {biz.bank_name&&<><div style={{fontWeight:900,marginBottom:4,textDecoration:'underline'}}>Bank Detail:</div>
                  <div><span style={{fontWeight:700}}>Bank Name</span> : {biz.bank_name}</div>
                  {biz.account_name&&<div><span style={{fontWeight:700}}>Account Name</span> : {biz.account_name}</div>}
                  {biz.account_number&&<div><span style={{fontWeight:700}}>Acct Number</span> : {biz.account_number}</div>}</>}
                </div>
                <div style={{fontSize:11}}>
                  <div style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',borderBottom:'1px solid #ccc'}}>
                    <span>សរុប (Total)</span><span style={{fontWeight:600}}>${subtotal.toFixed(2)}</span>
                  </div>
                  {disc>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',borderBottom:'1px solid #ccc'}}>
                    <span>បញ្ចុះតម្លៃ (Discount)</span><span style={{fontWeight:600}}>(${ disc.toFixed(2)})</span>
                  </div>}
                  <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',fontWeight:900,fontSize:13,background:'#f0f4ff'}}>
                    <span>សរុបរួម (Grand Total)</span><span>${total.toFixed(2)}</span>
                  </div>
                  <div style={{textAlign:'right',padding:'3px 10px',fontSize:9,color:'#888'}}>≈ ៛{fmtKHR(total)} (KHR)</div>
                </div>
              </div>
              {order.notes&&<div style={{border:'1px solid #ccc',borderTop:'none',padding:'5px 8px',fontSize:10,color:'#555'}}>📝 {order.notes}</div>}
              {/* Signatures */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:40,marginTop:24,marginBottom:8}}>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:12,fontWeight:700}}>ហត្ថលេខា និង ឈ្មោះអតិថិជន</div>
                  <div style={{fontSize:10,color:'#555',marginBottom:20}}>Customer's Signature and Name</div>
                  <div style={{borderTop:'1px solid #000'}}/>
                </div>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:12,fontWeight:700}}>ហត្ថលេខា និង ឈ្មោះអ្នកលក់</div>
                  <div style={{fontSize:10,color:'#555',marginBottom:20}}>Seller's Signature &amp; Name</div>
                  <div style={{borderTop:'1px solid #000'}}/>
                </div>
              </div>
              <div style={{textAlign:'center',marginTop:8,borderTop:'1px solid #ccc',paddingTop:6,fontSize:11}}>
                🙏 សូមអរគុណ! / Thank you! &nbsp;·&nbsp; <span style={{fontSize:9,color:'#999'}}>Page 1 of 1</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage(){
  const{hasPermission,isOwner,myBranchId,myBranch}=useAuth();
  const can={view:hasPermission('orders.view'),create:hasPermission('orders.create'),edit:hasPermission('orders.edit'),delete:hasPermission('orders.delete'),approve:hasPermission('orders.approve'),cancel:hasPermission('orders.cancel')};
  const w=useWindowWidth();
  const isMobile=w<768;
  const isTablet=w>=768&&w<1100;
  const {t} = useLang();
  const[data,setData]=useState([]);
  const[customers,setCustomers]=useState([]);
  const[products,setProducts]=useState([]);
  const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[statusF,setStatusF]=useState('All');
  const[isNew,setIsNew]=useState(false);
  const[panelOrder,setPanelOrder]=useState(null);
  const[printOrder,setPrintOrder]=useState(null);
  const[printSettings,setPrintSettings]=useState({});

  const[branchInventory,setBranchInventory]=useState([]);
  const[metaLoaded,setMetaLoaded]=useState(false);
  const[khrRate,setKhrRate]=useState(4100);
  const fmtKHR=n=>`≈ ៛${Math.round(n*khrRate).toLocaleString()}`;

  useEffect(()=>{
    setMetaLoaded(false);
    const branchParam=(!isOwner&&myBranchId)?myBranchId:null;
    const custParams={limit:500};
    Promise.allSettled([
      customerAPI.getAll(custParams),
      productAPI.getAll({limit:200,is_active:true}),
      branchAPI.getAll({limit:100}),
      branchParam?inventoryAPI.getAll({branch_id:branchParam,limit:1000}):Promise.resolve({data:{data:[]}}),
      currencyAPI.getRates(),
      settingsAPI.getAll(),
    ]).then(([cr,pr,br,ir,rr,sr])=>{
      if(cr.status==='fulfilled') setCustomers(cr.value.data.data||[]);
      if(pr.status==='fulfilled') setProducts(pr.value.data.data||[]);
      if(br.status==='fulfilled') setBranches(br.value.data.data||[]);
      if(ir.status==='fulfilled') setBranchInventory(ir.value.data.data||[]);
      if(rr.status==='fulfilled'){const rt=(rr.value?.data?.data||rr.value?.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setKhrRate(parseFloat(rt.rate)||4100);}
      if(sr.status==='fulfilled'){const o={};(sr.value.data.data||sr.value.data||[]).forEach(x=>{o[x.key]=x.value});setPrintSettings(o);}
      setMetaLoaded(true);
    });
  },[isOwner,myBranchId]);

  const load=useCallback(async()=>{
    try{const p={search,limit:200};if(statusF!=='All')p.status=statusF;if(!isOwner&&myBranchId)p.branch_id=myBranchId;const r=await orderAPI.getAll(p);setData(r.data.data||[])}
    catch{toast.error('Failed to load orders')}
    finally{setLd(false)}
  },[search,statusF,isOwner,myBranchId]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const openOrder=async o=>{
    try{const{data:d}=await orderAPI.getById(o.id);setPanelOrder(d);setIsNew(false);}
    catch{toast.error('Failed to load order')}
  };
  const openNew=()=>{setPanelOrder({});setIsNew(true);};
  const closePanel=()=>{setPanelOrder(null);setIsNew(false);};
  const onSaved=()=>{load();if(!isNew&&panelOrder)orderAPI.getById(panelOrder.id).then(r=>setPanelOrder(r.data)).catch(()=>{})};
  const onDeleted=()=>{closePanel();load();};

  const showPanel=panelOrder!==null||isNew;
  // On mobile, panel is full-screen overlay; on desktop it's side pane
  const showList=!isMobile||!showPanel;

  /* ── List column widths ── */
  const listWidth=showPanel
    ?(isTablet?'340px':'420px')
    :'100%';

  if(!can.view)return<NoPerm action="view sale orders"/>;
  return(
    <div style={{display:'flex',height:'calc(100vh - 60px)',overflow:'hidden',position:'relative'}}>

      {/* LIST PANE */}
      {showList&&(
        <div style={{display:'flex',flexDirection:'column',width:listWidth,minWidth:showPanel?'300px':undefined,
          flexShrink:0,borderRight:showPanel?'1px solid var(--border)':'none',
          transition:'width .2s',overflow:'hidden',background:'var(--surface)'}}>

          {/* Toolbar */}
          <div style={{padding:isMobile?'14px 14px 0':'16px 16px 0',flexShrink:0}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <div>
                <div style={{fontSize:isMobile?16:18,fontWeight:800}}>{t('orders.title')}</div>
                <div style={{fontSize:12,color:'var(--td)',marginTop:1}}>{data.length} orders</div>
              </div>
              {can.create&&<button className="btn btn-primary" onClick={openNew}
                style={{gap:5,height:38,fontSize:13,padding:'0 14px'}}>
                <Plus size={14}/>{!isMobile&&' '+t('orders.newOrder')}
              </button>}
            </div>
            <SearchBar value={search} onChange={setSearch} placeholder={t('orders.searchOrders')}/>
            <div style={{marginTop:8,overflowX:'auto',paddingBottom:4,marginLeft:-2}}>
              <FilterButtons options={FILTER_TABS} active={statusF} onChange={setStatusF}/>
            </div>
          </div>

          {/* Order Cards */}
          <div style={{flex:1,overflowY:'auto',padding:isMobile?'8px 10px':'8px'}}>
            {ld?(
              <div style={{padding:40,textAlign:'center',color:'var(--td)',fontSize:13}}>Loading…</div>
            ):data.length===0?(
              <div style={{padding:40,textAlign:'center',color:'var(--td)',fontSize:13}}>
                <FileText size={32} style={{opacity:.2,marginBottom:12,display:'block',margin:'0 auto 12px'}}/>
                {t('orders.noOrders')}
              </div>
            ):data.map(o=>{
              const isActive=!isNew&&panelOrder?.id===o.id;
              return(
                <div key={o.id} onClick={()=>openOrder(o)}
                  style={{padding:isMobile?'13px 14px':'12px 14px',borderRadius:10,marginBottom:4,cursor:'pointer',
                    border:`1.5px solid ${isActive?'var(--primary)':'var(--border)'}`,
                    background:isActive?'var(--pm)':'var(--surface)',transition:'all .12s'}}
                  onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background='var(--s2)'}}
                  onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='var(--surface)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                    <span style={{fontSize:13,fontWeight:700,color:isActive?'var(--primary)':'var(--text)',flex:1}}>{o.order_number}</span>
                    <SPill s={o.status} size={10}/>
                    {o.status==='completed'&&(
                      <span style={{fontSize:9,fontWeight:700,color:'var(--success)',background:'var(--sm)',padding:'2px 6px',borderRadius:8,letterSpacing:.3}}>INVOICED</span>
                    )}
                    {isActive&&!isMobile&&<ChevronRight size={14} color="var(--primary)"/>}
                  </div>
                  <div style={{fontSize:13,color:'var(--td)',marginBottom:4,fontWeight:500}}>{o.customer?.name||'—'}</div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:11,color:'var(--td)'}}>{fmtDate(o.order_date)} · {o.items?.length||0} item{(o.items?.length||0)!==1?'s':''}</span>
                    <div style={{textAlign:'right'}}><span style={{fontSize:14,fontWeight:800,color:'var(--text)'}}>{fmt(o.total_amount)}</span><div style={{fontSize:10,color:'var(--td)',marginTop:1}}>{fmtKHR(parseFloat(o.total_amount||0))}</div></div>
                  </div>
                  <div style={{display:'flex',gap:10,marginTop:4,flexWrap:'wrap'}}>
                    {o.creator&&<span style={{fontSize:10,color:'var(--td)',display:'flex',alignItems:'center',gap:3}}>
                      <span style={{color:'var(--tm)'}}>By:</span> {o.creator.first_name} {o.creator.last_name}
                    </span>}
                    {o.updater&&<span style={{fontSize:10,color:'var(--td)',display:'flex',alignItems:'center',gap:3}}>
                      <span style={{color:'var(--tm)'}}>Edited:</span> {o.updater.first_name} {o.updater.last_name}
                    </span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DETAIL PANE */}
      {showPanel&&(
        <div style={{
          flex:isMobile?undefined:1,
          width:isMobile?'100%':undefined,
          ...(isMobile?{position:'absolute',inset:0,zIndex:100,background:'var(--surface)'}:{}),
          overflow:'hidden',display:'flex',flexDirection:'column'}}>
          {!metaLoaded?(
            <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--td)',fontSize:13,gap:8}}>
              <RefreshCw size={16} style={{animation:'spin 1s linear infinite'}}/> Loading…
            </div>
          ):(
            <OrderPanel
              key={isNew?'new':panelOrder?.id}
              order={panelOrder||{}}
              isNew={isNew}
              customers={customers}
              products={products}
              branches={isOwner?branches:branches.filter(b=>Number(b.id)===Number(myBranchId))}
              branchInventory={branchInventory}
              onSaved={onSaved}
              onDeleted={onDeleted}
              onClose={closePanel}
              onPrint={o=>setPrintOrder(o)}
              isMobile={isMobile}
            />
          )}
        </div>
      )}
      {printOrder&&<PrintOrderModal order={printOrder} settings={printSettings} onClose={()=>setPrintOrder(null)}/>}
    </div>
  );
}
